import React from 'react';

import { useAcknowledgeAnnouncementMutation, useGetPendingAnnouncementsQuery } from '@/api/v1';
import { Announcement } from '@/types/announcement';

interface UseAnnouncementsResult {
  readonly announcement: Announcement | null;
  readonly visible: boolean;
  readonly acknowledging: boolean;
  readonly error: boolean;
  acknowledge(): void;
  dismiss(): void;
}

const SHOW_DELAY_MS = 500;

export function useAnnouncements(): UseAnnouncementsResult {
  const { data } = useGetPendingAnnouncementsQuery();
  const [acknowledge, { isLoading: acknowledging, isError }] = useAcknowledgeAnnouncementMutation();

  const [visible, setVisible] = React.useState(false);

  const announcement: Announcement | null = data?.announcements?.[0] ?? null;

  React.useEffect((): (() => void) | undefined => {
    if (!announcement) {
      return;
    }

    const timer: ReturnType<typeof setTimeout> = setTimeout(
      (): void => setVisible(true),
      SHOW_DELAY_MS,
    );
    return (): void => clearTimeout(timer);
  }, [announcement]);

  const handleAcknowledge = React.useCallback((): void => {
    if (!announcement) {
      return;
    }

    acknowledge(announcement.id)
      .unwrap()
      .then((): void => setVisible(false))
      .catch((): void => {
        // Keep modal open — error state shown via `isError`
      });
  }, [announcement, acknowledge]);

  const handleDismiss = React.useCallback((): void => {
    setVisible(false);
  }, []);

  return {
    announcement,
    visible,
    acknowledging,
    error: isError,
    acknowledge: handleAcknowledge,
    dismiss: handleDismiss,
  };
}
